import React, { useState, useEffect } from 'react'
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Collapse,
    Alert,
    CircularProgress,
    Autocomplete
} from '@mui/material'
import {
    Search,
    FilterList,
    Download,
    ExpandMore,
    ExpandLess,
    Refresh
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import PageHeader from '../components/ui/PageHeader'
import reportingService from '../services/reportingService'
import { apiClient } from '../services/api'
import type { AuditLog, AuditLogFilters } from '../services/reportingService'

interface AdminUser {
    id: string
    username: string
    email: string
    full_name: string
}

const AuditLogViewerMUI: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([])
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [expandedRow, setExpandedRow] = useState<string | null>(null)
    const [showFilters, setShowFilters] = useState(false)

    // Admin users for filtering
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
    const [loadingUsers, setLoadingUsers] = useState(false)

    // Available actions (populated from logs)
    const [availableActions, setAvailableActions] = useState<string[]>([])

    // Filter states
    const [eventType, setEventType] = useState('')
    const [eventCategory, setEventCategory] = useState('')
    const [actorType, setActorType] = useState('')
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
    const [action, setAction] = useState('')
    const [resourceType, setResourceType] = useState('')
    const [status, setStatus] = useState('')
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)

    useEffect(() => {
        loadAdminUsers()
        loadAvailableActions()
    }, [])

    useEffect(() => {
        loadAuditLogs()
    }, [page, rowsPerPage])

    const loadAdminUsers = async () => {
        setLoadingUsers(true)
        try {
            const response = await apiClient.get('/admin/admin-users', {
                params: { page: 1, page_size: 100 }  // Changed from 1000 to 100 (API limit)
            })
            setAdminUsers(response.data.users || [])
        } catch (error) {
            console.error('Error loading admin users:', error)
        } finally {
            setLoadingUsers(false)
        }
    }

    const loadAvailableActions = async () => {
        try {
            // Get unique actions from recent logs (limited to 100 due to API constraint)
            const response = await reportingService.getAuditLogs({ limit: 100 })
            const actions = Array.from(new Set(response.logs.map(log => log.action)))
            setAvailableActions(actions.sort())
        } catch (error) {
            console.error('Error loading available actions:', error)
            // Set empty array on error to prevent UI issues
            setAvailableActions([])
        }
    }

    const loadAuditLogs = async () => {
        setLoading(true)
        try {
            const filters: AuditLogFilters = {
                event_type: eventType || undefined,
                event_category: eventCategory || undefined,
                actor_type: actorType || undefined,
                actor_id: selectedUser?.id || undefined,
                action: action || undefined,
                resource_type: resourceType || undefined,
                status: status || undefined,
                start_date: startDate?.toISOString().split('T')[0],
                end_date: endDate?.toISOString().split('T')[0],
                limit: rowsPerPage,
                offset: page * rowsPerPage
            }

            const response = await reportingService.getAuditLogs(filters)
            setLogs(response.logs)
            setTotalCount(response.total)  // Changed from total_count to total
        } catch (error) {
            console.error('Error loading audit logs:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        setPage(0)
        loadAuditLogs()
    }

    const handleReset = () => {
        setEventType('')
        setEventCategory('')
        setActorType('')
        setSelectedUser(null)
        setAction('')
        setResourceType('')
        setStatus('')
        setStartDate(null)
        setEndDate(null)
        setPage(0)
        loadAuditLogs()
    }

    const handleExport = async () => {
        try {
            const filters: AuditLogFilters = {
                event_type: eventType || undefined,
                event_category: eventCategory || undefined,
                actor_type: actorType || undefined,
                actor_id: selectedUser?.id || undefined,
                action: action || undefined,
                resource_type: resourceType || undefined,
                status: status || undefined,
                start_date: startDate?.toISOString().split('T')[0],
                end_date: endDate?.toISOString().split('T')[0],
            }

            const blob = await reportingService.exportAuditLogs({ ...filters, format: 'csv' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `audit_logs_${Date.now()}.csv`
            a.click()
            URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Error exporting audit logs:', error)
            alert('Error exporting audit logs. Please try again.')
        }
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const toggleRowExpansion = (logId: string) => {
        setExpandedRow(expandedRow === logId ? null : logId)
    }

    const getStatusColor = (status: string) => {
        return status === 'success' ? 'success' : 'error'
    }

    const formatDateTime = (dateString: string) => {
        if (!dateString) return 'N/A'
        
        try {
            const date = new Date(dateString)
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Invalid Date'
            }
            
            // Format: DD/MM/YYYY, HH:MM:SS
            return date.toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            })
        } catch (error) {
            console.error('Error formatting date:', dateString, error)
            return 'Invalid Date'
        }
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                <PageHeader
                    title="Audit Log Viewer"
                    subtitle="Comprehensive audit trail with advanced filtering and search"
                    actions={[
                        <Button
                            key="filter"
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>,
                        <Button
                            key="refresh"
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={loadAuditLogs}
                        >
                            Refresh
                        </Button>,
                        <Button
                            key="export"
                            variant="contained"
                            startIcon={<Download />}
                            onClick={handleExport}
                        >
                            Export CSV
                        </Button>
                    ] as any}
                />

                {/* Filters Section */}
                <Collapse in={showFilters}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Filter Audit Logs
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Event Type</InputLabel>
                                        <Select
                                            value={eventType}
                                            label="Event Type"
                                            onChange={(e) => setEventType(e.target.value)}
                                        >
                                            <MenuItem value="">All Types</MenuItem>
                                            <MenuItem value="admin_action">Admin Action</MenuItem>
                                            <MenuItem value="customer_action">Customer Action</MenuItem>
                                            <MenuItem value="system_event">System Event</MenuItem>
                                            <MenuItem value="security_event">Security Event</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            value={eventCategory}
                                            label="Category"
                                            onChange={(e) => setEventCategory(e.target.value)}
                                        >
                                            <MenuItem value="">All Categories</MenuItem>
                                            <MenuItem value="authentication">Authentication</MenuItem>
                                            <MenuItem value="product">Product</MenuItem>
                                            <MenuItem value="order">Order</MenuItem>
                                            <MenuItem value="customer">Customer</MenuItem>
                                            <MenuItem value="payment">Payment</MenuItem>
                                            <MenuItem value="system">System</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Actor Type</InputLabel>
                                        <Select
                                            value={actorType}
                                            label="Actor Type"
                                            onChange={(e) => setActorType(e.target.value)}
                                        >
                                            <MenuItem value="">All Actors</MenuItem>
                                            <MenuItem value="admin">Admin</MenuItem>
                                            <MenuItem value="customer">Customer</MenuItem>
                                            <MenuItem value="system">System</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Autocomplete
                                        size="small"
                                        options={adminUsers}
                                        getOptionLabel={(option) => `${option.full_name || option.username} (${option.email})`}
                                        value={selectedUser}
                                        onChange={(_, newValue) => setSelectedUser(newValue)}
                                        loading={loadingUsers}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Filter by User"
                                                placeholder="Select admin user"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={status}
                                            label="Status"
                                            onChange={(e) => setStatus(e.target.value)}
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            <MenuItem value="success">Success</MenuItem>
                                            <MenuItem value="failure">Failure</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Autocomplete
                                        size="small"
                                        freeSolo
                                        options={availableActions}
                                        value={action}
                                        onChange={(_, newValue) => setAction(newValue || '')}
                                        onInputChange={(_, newValue) => setAction(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Action"
                                                placeholder="Select or type action"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Resource Type"
                                        value={resourceType}
                                        onChange={(e) => setResourceType(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={setStartDate}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={setEndDate}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" gap={1} justifyContent="flex-end">
                                        <Button variant="outlined" onClick={handleReset}>
                                            Reset
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<Search />}
                                            onClick={handleSearch}
                                        >
                                            Apply Filters
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Collapse>

                {/* Audit Logs Table */}
                <Card>
                    <CardContent>
                        {loading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : logs.length === 0 ? (
                            <Alert severity="info">No audit logs found matching the criteria</Alert>
                        ) : (
                            <>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Timestamp</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Actor</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Action</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Resource</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Details</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {logs.map((log) => (
                                                <React.Fragment key={log.id}>
                                                    <TableRow hover>
                                                        <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <Typography variant="body2">{log.actor_identifier}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {log.actor_type}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={log.action} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <Typography variant="body2">{log.resource_type}</Typography>
                                                                {log.resource_id && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        ID: {log.resource_id}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={log.status}
                                                                size="small"
                                                                color={getStatusColor(log.status) as any}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleRowExpansion(log.id)}
                                                            >
                                                                {expandedRow === log.id ? <ExpandLess /> : <ExpandMore />}
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ p: 0 }}>
                                                            <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                                                                <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                                                    <Typography variant="subtitle2" gutterBottom>
                                                                        Event Details
                                                                    </Typography>
                                                                    <Grid container spacing={2}>
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Event Type
                                                                            </Typography>
                                                                            <Typography variant="body2">{log.event_type}</Typography>
                                                                        </Grid>
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Event Category
                                                                            </Typography>
                                                                            <Typography variant="body2">{log.event_category}</Typography>
                                                                        </Grid>
                                                                        {log.ip_address && (
                                                                            <Grid item xs={12} md={6}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    IP Address
                                                                                </Typography>
                                                                                <Typography variant="body2">{log.ip_address}</Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {log.user_agent && (
                                                                            <Grid item xs={12} md={6}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    User Agent
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                                                    {log.user_agent}
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        <Grid item xs={12}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Additional Details
                                                                            </Typography>
                                                                            <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                                                                                <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                                                                                    {JSON.stringify(log.details, null, 2)}
                                                                                </pre>
                                                                            </Paper>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </LocalizationProvider>
    )
}

export default AuditLogViewerMUI